import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Mail, User } from "lucide-react";
import { useForgotPasswordMutation } from "@/store/ChangPasswordApi";
import { toast } from "sonner";
import logo from "../assets/robotwala1.png";
const ForgotPassword = () => {
  const [formData, setFormData] = useState({
    email: "",
    role: "",
  });

  const navigate = useNavigate();
  const [forgotPassword, { isLoading: forgotPasswordLoading }] =
    useForgotPasswordMutation();

  const handleSubmit = async () => {
    const { email, role } = formData;
    if (!email || !role) return alert("Please enter all fields");

    try {
      const res = await forgotPassword(formData);

      if (res?.data) {
        toast.success(res?.message || "OTP sent to email successfully!!");
        navigate("/reset-password", { state: { email, role } });
      } else {
        toast.error(res?.error?.data?.message || "something went wrong !");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-black dark:to-black p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img src={logo} alt="logo" className="h-24 w-auto" />
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mb-2">
              <Mail className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Forgot Password?
            </h1>
            <p className="text-slate-600 dark:text-slate-300 text-sm">
              No worries! Enter your email and role, and we’ll send you reset
              instructions.
            </p>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Email Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="pl-10"
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
              </div>
            </div>

            {/* Role Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Role
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                <div className="pl-10">
                  <Select
                    value={formData.role}
                    onValueChange={(value) =>
                      setFormData({ ...formData, role: value })
                    }
                  >
                    <SelectTrigger className="text-foreground">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* <SelectItem value="admin">Admin</SelectItem> */}
                      <SelectItem value="hr">HR</SelectItem>
                      <SelectItem value="instructor">Instructor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              className="w-full h-11 text-base font-semibold"
              disabled={
                forgotPasswordLoading || !formData.email || !formData.role
              }
            >
              {forgotPasswordLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </div>

          {/* Back to Login */}
          <div className="text-center">
            <button
              className="text-sm text-primary hover:underline font-medium inline-flex items-center gap-1"
              onClick={() => navigate("/login")}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
